/**
 * The source-mix panel under a saved Co-op campaign's "Max Facebook share of
 * daily cap" field (epic 091 D12 / D13, task 05): how this campaign's Facebook
 * and CTO leads sold over the server's default range, so Chelsie can read it
 * before and after she sets a share.
 *
 * Read-only and never in the form's way: one fetch per campaign (the latest
 * response wins, an unmounted panel applies nothing), and a failed fetch is one
 * quiet muted line, never a toast or a form error.
 */

import { useEffect, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { fmtCount, fmtDate, fmtMoney } from '../../lib/format';
import {
  getCoopSourceMix,
  type CoopSourceMixDay,
  type CoopSourceMixResponse,
  type SourceKey,
} from './source-mix-api';

/** Facebook and CTO always read; Other only when it has something to say. */
const SOURCES = [
  { key: 'facebook', label: 'Facebook' },
  { key: 'cto', label: 'CTO' },
  { key: 'other', label: 'Other' },
] as const satisfies readonly { key: SourceKey; label: string }[];

const NO_VALUE = '—';

const MUTED = 'text-xs text-muted';

type Load =
  | { campaignId: string; kind: 'ready'; mix: CoopSourceMixResponse }
  | { campaignId: string; kind: 'failed' };

/** sold / offered as a whole percent. */
function pct(sold: number, offered: number): string {
  return `${Math.round((sold / offered) * 100)}%`;
}

/** The summary line, sold and offered over the whole range, per source. */
function campaignSummary(mix: CoopSourceMixResponse): string {
  const { campaign: counts } = mix.totals;
  const hasPrice = mix.campaign.pricePerLead !== null;
  let spelledOut = false;
  const parts = SOURCES.filter(({ key }) => key !== 'other' || counts.other.offered > 0).map(
    ({ key, label }) => {
      const { offered, sold, earned } = counts[key];
      if (offered === 0) return `${label} ${NO_VALUE}`;
      const rates = hasPrice
        ? `${pct(sold, offered)}, ${fmtMoney(earned / offered)} a lead`
        : pct(sold, offered);
      const figures = spelledOut
        ? `${fmtCount(sold)} of ${fmtCount(offered)}`
        : `${fmtCount(sold)} sold of ${fmtCount(offered)} offered`;
      spelledOut = true;
      return `${label} ${figures} (${rates})`;
    }
  );
  return `Last ${mix.days.length} days, this campaign: ${parts.join(' · ')}`;
}

/** The advertiser line: every campaign of the advertiser, percent only (prices differ). */
function advertiserSummary(mix: CoopSourceMixResponse): string {
  const { advertiser: counts } = mix.totals;
  const parts = SOURCES.filter(({ key }) => key !== 'other' || counts.other.offered > 0).map(
    ({ key, label }) => {
      const { offered, sold } = counts[key];
      return `${label} ${offered === 0 ? NO_VALUE : pct(sold, offered)}`;
    }
  );
  return `All ${mix.campaign.advertiserName} campaigns: ${parts.join(' · ')}`;
}

function turnedAway(day: CoopSourceMixDay): string {
  const { byShare, bySiteBlock } = day.facebookTurnedAway;
  if (byShare === 0 && bySiteBlock === 0) return NO_VALUE;
  return `${fmtCount(byShare)} share + ${fmtCount(bySiteBlock)} site block`;
}

function soldElsewhere(day: CoopSourceMixDay): string {
  const { byShare, bySiteBlock, soldElsewhere: sold } = day.facebookTurnedAway;
  return byShare === 0 && bySiteBlock === 0 ? NO_VALUE : fmtCount(sold);
}

function DayRow({
  day,
  dailyCap,
  showOther,
}: {
  day: CoopSourceMixDay;
  dailyCap: number | null;
  showOther: boolean;
}) {
  return (
    <TableRow>
      <TableCell className="whitespace-nowrap">{fmtDate(day.date)}</TableCell>
      <TableCell className="text-right">{fmtCount(day.sources.facebook.sold)}</TableCell>
      <TableCell className="text-right">{fmtCount(day.sources.cto.sold)}</TableCell>
      {showOther && (
        <TableCell className="text-right">{fmtCount(day.sources.other.sold)}</TableCell>
      )}
      <TableCell className="text-right">{fmtMoney(day.earnedTotal)}</TableCell>
      <TableCell className="whitespace-nowrap text-right">
        {dailyCap === null ? (
          NO_VALUE
        ) : (
          <>
            {fmtCount(day.soldTotal)}/{fmtCount(dailyCap)}
            {day.capHit && (
              <sup data-testid="cap-hit" className="ml-0.5 text-[10px] text-faint">
                capped
              </sup>
            )}
          </>
        )}
      </TableCell>
      <TableCell className="whitespace-nowrap text-right">{turnedAway(day)}</TableCell>
      <TableCell className="text-right">{soldElsewhere(day)}</TableCell>
    </TableRow>
  );
}

function MixTable({ mix }: { mix: CoopSourceMixResponse }) {
  const showOther = mix.days.some((day) => day.sources.other.sold > 0);
  const newestFirst = [...mix.days].reverse();
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Date</TableHead>
          <TableHead className="text-right">Facebook sold</TableHead>
          <TableHead className="text-right">CTO sold</TableHead>
          {showOther && <TableHead className="text-right">Other sold</TableHead>}
          <TableHead className="text-right">Earned</TableHead>
          <TableHead className="text-right">Cap</TableHead>
          <TableHead className="text-right">Facebook turned away</TableHead>
          <TableHead className="text-right">{'…'}sold elsewhere</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {newestFirst.map((day) => (
          <DayRow key={day.date} day={day} dailyCap={mix.campaign.dailyCap} showOther={showOther} />
        ))}
      </TableBody>
    </Table>
  );
}

interface SourceMixPanelProps {
  campaignId: string;
}

export function SourceMixPanel({ campaignId }: SourceMixPanelProps) {
  const [load, setLoad] = useState<Load | null>(null);

  useEffect(() => {
    // Cleared by the cleanup when another campaign (or unmount) supersedes this fetch.
    let latest = true;
    getCoopSourceMix(campaignId).then(
      (mix) => {
        if (latest) setLoad({ campaignId, kind: 'ready', mix });
      },
      () => {
        if (latest) setLoad({ campaignId, kind: 'failed' });
      }
    );
    return () => {
      latest = false;
    };
  }, [campaignId]);

  if (load === null || load.campaignId !== campaignId) {
    return <p className={MUTED}>Loading how this campaign's leads sold{'…'}</p>;
  }
  if (load.kind === 'failed') {
    return <p className={MUTED}>Couldn't load the source mix.</p>;
  }
  return (
    <div data-testid="source-mix-panel" className="space-y-3">
      <div className="space-y-1 text-sm">
        <p>{campaignSummary(load.mix)}</p>
        <p className="text-muted">{advertiserSummary(load.mix)}</p>
      </div>
      <MixTable mix={load.mix} />
      <p className={MUTED}>
        Earned = sales {'×'} today's price per lead. Our EPL: Stats {'›'} Daily Spend {'›'} Co-op
        EPL.
      </p>
    </div>
  );
}
